import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";

export interface Location {
  longitude: number;
  latitude: number;
}

export interface User {
  id: string;
  username: string;
  display_name: string;
  email: string;
  first_name: string;
  last_name: string;
  bio: string;
  website?: string;
  gender?: "male" | "female" | "non_binary" | "prefer_not_to_say" | "other";
  emails_of_related_user?: string[];
  emergency_contacts?: string[];
  location_name?: string;
  date_of_birth?: string;
  avatar_url?: string;
  is_staff: boolean;
  is_verified: boolean;
  joined_at: string;
  last_login: string;
  post_count: number;
  communities: unknown[];
  location?: Location;
  notification_settings?: {
    email_digest?: boolean;
    push_notifications?: boolean;
    mention_notifications?: boolean;
    reply_notifications?: boolean;
  };
  preferences?: {
    theme?: "light" | "dark";
    language?: string;
  };
  privacy_settings?: {
    profile_visibility?: "public" | "members" | "private";
    show_email?: boolean;
    show_location?: boolean;
    allow_friend_requests?: boolean;
    email_notifications?: boolean;
    push_notifications?: boolean;
  };
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export const defaultAuthState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

// Base atom with storage
export const authStateAtom = atomWithStorage<AuthState>(
  "auth",
  defaultAuthState,
);

// Derived atoms for individual state properties
export const userAtom = atom(
  (get) => get(authStateAtom).user,
  (get, set, newUser: User | null) => {
    set(authStateAtom, {
      ...get(authStateAtom),
      user: newUser,
      isAuthenticated: !!newUser,
    });
  },
);

export const isAuthenticatedAtom = atom(
  (get) => get(authStateAtom).isAuthenticated,
);

export const isLoadingAtom = atom(
  (get) => get(authStateAtom).isLoading,
  (get, set, isLoading: boolean) => {
    set(authStateAtom, {
      ...get(authStateAtom),
      isLoading,
    });
  },
);

export const errorAtom = atom(
  (get) => get(authStateAtom).error,
  (get, set, error: string | null) => {
    set(authStateAtom, {
      ...get(authStateAtom),
      error,
    });
  },
);

// Auth actions using atom getters and setters
export const useAuthActions = (
  get: (atom: unknown) => AuthState,
  set: (atom: unknown, value: AuthState) => void,
) => ({
  setUser: (user: User | null) => {
    set(authStateAtom, {
      user,
      isAuthenticated: !!user,
      isLoading: false,
      error: null,
    });
  },

  setError: (error: string) => {
    set(authStateAtom, {
      ...defaultAuthState,
      error,
    });
  },

  setLoading: (isLoading: boolean) => {
    const currentState = get(authStateAtom);
    set(authStateAtom, {
      ...currentState,
      isLoading,
      error: null,
    });
  },

  logout: () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("refresh_token");
    set(authStateAtom, defaultAuthState);
  },
});
